import "./App.css";
import { Suspense, lazy, useEffect, useLayoutEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { locations } from "./data/locations";
import { countySlugs, serviceSlugs as allServiceSlugs, legalSlugs as allLegalSlugs } from "./data/routeSlugs";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
const BranchAlias = lazy(() => import("./components/BranchAlias"));
const TownCityAlias = lazy(() => import("./components/TownCityAlias"));
import TrailingSlashRedirect from "./components/TrailingSlashRedirect";
import KeepQueryNavigate from "./components/KeepQueryNavigate";

// 301-style redirects from dead old-design URLs to their new canonical homes.
// React Router's <Navigate replace> sends a client-side replace that Googlebot
// (running headless Chromium) correctly treats as a permanent URL substitution,
// passing link equity to the destination.
const LEGACY_REDIRECTS = [
  // Old FAQ/reviews/services hubs
  { from: "/faq", to: "/faqs" },
  { from: "/testimonials", to: "/#google-reviews-section" },
  { from: "/reviews", to: "/#google-reviews-section" },
  { from: "/quote", to: "/get-a-quote" },
  { from: "/get-quote", to: "/get-a-quote" },
  { from: "/free-quote", to: "/get-a-quote" },
  { from: "/book", to: "/get-a-quote" },
  { from: "/home", to: "/" },
  { from: "/index", to: "/" },
  { from: "/index.html", to: "/" },

  // Old info pages
  { from: "/help", to: "/contact" },
  { from: "/accessibility", to: "/" },
  { from: "/international-moving", to: "/long-distance-removals" },
  { from: "/blog/international-moving-uk", to: "/long-distance-removals" },
  { from: "/tips-and-tricks", to: "/blog" },

  // GSC "duplicate without canonical" cleanup (Aug 2026) — retired blog slugs → nearest live guide
  { from: "/blog/2", to: "/blog" },
  { from: "/blog/3", to: "/blog" },
  { from: "/blog/house-move-checklist", to: "/blog/ultimate-moving-home-checklist-uk-2026" },
  { from: "/blog/moving-cost-uk-guide", to: "/blog/how-much-do-removals-cost-slough-berkshire" },
  { from: "/blog/packing-tips-fragile", to: "/blog/packing-fragile-items-professional-guide" },
  { from: "/blog/moving-with-kids", to: "/blog/moving-home-with-kids-and-pets" },
  { from: "/blog/best-time-to-move-house", to: "/blog/best-day-to-move-house-uk" },
  { from: "/blog/removals-vs-self-move", to: "/blog/man-and-van-vs-full-removals-which-to-choose" },
  { from: "/blog/first-time-buyer-moving", to: "/blog/buying-first-home-small-deposit-uk" },
  { from: "/blog/decluttering-before-move", to: "/blog/preparing-home-for-sale-top-tips" },
  { from: "/blog/downsizing-guide", to: "/blog/preparing-home-for-sale-top-tips" },
  { from: "/blog/stress-free-moving-day", to: "/blog/moving-home-tips-from-professional-movers" },
  { from: "/blog/insurance-for-removals", to: "/blog/moving-home-tips-from-professional-movers" },
  { from: "/blog/office-relocation-guide", to: "/office-relocation" },
  { from: "/blog/storage-vs-removals", to: "/blog" },
  { from: "/removals-witney", to: "/removals-oxfordshire" },
  // GSC "excluded by noindex" cleanup (Aug 2026) — legacy service/info URLs → live pages
  { from: "/storage", to: "/removals-and-storage" },
  { from: "/residential-removals", to: "/house-removals" },
  { from: "/same-day-removals-slough", to: "/removals-slough" },
  { from: "/same-day-removals-london", to: "/london-removals" },
  { from: "/piano-moving-london", to: "/london-removals" },
  { from: "/student-removals-london", to: "/london-removals" },
  { from: "/vehicle-guide", to: "/our-fleet" },
  { from: "/affiliate-program", to: "/contact" },
  { from: "/about-founder", to: "/about" },
  { from: "/sustainability", to: "/environmental-policy" },
  { from: "/insurance", to: "/faqs" },
  { from: "/trust-credentials", to: "/about" },
  { from: "/terms-conditions", to: "/terms" },
  { from: "/terms-and-conditions", to: "/terms" },
  { from: "/privacy", to: "/privacy-policy" },
  { from: "/cookies", to: "/cookies-policy" },
  { from: "/cookie", to: "/cookies-policy" },
  { from: "/cookie-policy", to: "/cookies-policy" },
  { from: "/gdpr", to: "/privacy-policy" },
  { from: "/complaints-procedure", to: "/complaints" },

  // Old location+service combo URLs (now real pages — redirect-only the singular legacy slug)
  { from: "/packing-service-slough", to: "/packing-services-slough" },
  { from: "/office-removals", to: "/office-relocation" },
  { from: "/office-removals-birmingham", to: "/office-relocation" },
  { from: "/office-removals-london", to: "/office-relocation" },
  { from: "/student-removals-manchester", to: "/" },
  // Service discontinued: /man-and-van (and all combos) — redirect to House Removals
  { from: "/man-and-van", to: "/house-removals" },

  // Old /town/* → new /removals-* (Windsor served; others not — to home)

  // Old /city/* (not served anymore — to homepage)

  // Old county pages /removals/* (served counties → areas; unserved → home)
  { from: "/removals/berkshire", to: "/areas-we-cover" },
  { from: "/removals/buckinghamshire", to: "/areas-we-cover" },
  { from: "/removals/middlesex", to: "/areas-we-cover" },
  { from: "/removals/oxfordshire", to: "/areas-we-cover" },
  { from: "/removals/hertfordshire", to: "/areas-we-cover" },
  { from: "/removals/bedfordshire", to: "/" },
  { from: "/removals/cambridgeshire", to: "/" },
  { from: "/removals/cheshire", to: "/" },
  { from: "/removals/cornwall", to: "/" },
  { from: "/removals/derbyshire", to: "/" },
  { from: "/removals/devon", to: "/" },
  { from: "/removals/essex", to: "/" },
  { from: "/removals/gloucestershire", to: "/" },
  { from: "/removals/hampshire", to: "/" },
  { from: "/removals/kent", to: "/" },
  { from: "/removals/lancashire", to: "/" },
  { from: "/removals/lincolnshire", to: "/" },
  { from: "/removals/norfolk", to: "/" },
  { from: "/removals/northamptonshire", to: "/" },
  { from: "/removals/nottinghamshire", to: "/" },
  { from: "/removals/somerset", to: "/" },
  { from: "/removals/suffolk", to: "/" },
  { from: "/removals/sussex", to: "/" },
  { from: "/removals/warwickshire", to: "/" },
  { from: "/removals/wiltshire", to: "/" },
  { from: "/removals/worcestershire", to: "/" },
  { from: "/removals/east-riding-of-yorkshire", to: "/" },

  // SEO Blueprint aliases (May 2026) — Apex/Pickfords-style URL variants pointing
  // at our existing canonical pages so we capture competitor-style search traffic
  // without cannibalising our already-indexed Google ranking equity.
  { from: "/house-removals-uk", to: "/house-removals" },
  { from: "/office-relocation-uk", to: "/office-relocation" },
  { from: "/packing-moving-services", to: "/packing-services" },
  { from: "/furniture-assembly-service", to: "/furniture-assembly" },
  { from: "/commercial-movers-uk", to: "/office-relocation" },
  { from: "/moving-quotes", to: "/get-a-quote" },
  // NOTE: /removals-london is now a real CountyTemplate hub (added 2026-05-29), not an alias.
  // Old line removed: { from: "/removals-london", to: "/uxbridge-removals" }

  // Old blog posts → matching new post or /blog index
  { from: "/blog/1", to: "/blog" },
  { from: "/blog/burnham-removals-guide-2026", to: "/blog" },
  { from: "/blog/choose-reliable-removal-company-uk", to: "/blog" },
  { from: "/blog/house-moving-tips-2025", to: "/blog/ultimate-moving-home-checklist-uk-2026" },
  { from: "/blog/packing-guide-house-removals", to: "/blog/packing-fragile-items-professional-guide" },
  { from: "/blog/uk-house-removals-cost-2026", to: "/blog/how-much-do-removals-cost-slough-berkshire" },
];

// Global image fallback — swaps any broken <img> (Unsplash 404/429, expired CDN URLs)
// with the HomeGo logo to avoid visible breakage. Uses capture phase since `error`
// events don't bubble naturally for <img>.
const FALLBACK_IMG = "/homego-logo.webp";
function useGlobalImageFallback() {
  useEffect(() => {
    const handler = (e) => {
      const el = e.target;
      if (el && el.tagName === "IMG" && el.dataset.fallback !== "done") {
        el.dataset.fallback = "done";
        el.src = FALLBACK_IMG;
        el.removeAttribute("srcset");
        el.style.objectFit = "contain";
        el.style.background = "#000";
        el.style.padding = "12px";
      }
    };
    window.addEventListener("error", handler, true);
    return () => window.removeEventListener("error", handler, true);
  }, []);
}

// Global tel: click delegate — fires `trackPhoneClick()` (which dispatches the
// GA4 `phone_click` event + every configured Google Ads phone-click conversion
// label across all 3 Ads accounts) on ANY <a href="tel:…"> click site-wide.
// Lets us count phone-call conversions without manually wiring onClick on the
// 20+ components that render phone numbers (Footer, Burnham/Birmingham/Uxbridge
// pages, BranchSubPage, LocationTemplate, ServiceTemplate, etc.). Document-level
// listener catches both direct clicks and any programmatic .click() calls.
function useGlobalPhoneClickTracking() {
  useEffect(() => {
    let analyticsRef = null;
    const handler = (e) => {
      // Walk up to find the nearest <a>
      const anchor = e.target && e.target.closest && e.target.closest('a[href^="tel:"]');
      if (!anchor) return;
      const phone = anchor.getAttribute('href').replace(/^tel:/i, '').trim();
      // Lazy-load analytics module on first phone click to avoid pulling it
      // into the initial bundle for users who never tap a phone number.
      const fire = (mod) => {
        try {
          mod.trackPhoneClick({
            phone,
            label: anchor.dataset.trackLabel || 'global-delegate',
            branch: anchor.dataset.trackBranch || null,
          });
        } catch (_) { /* never break UX */ }
      };
      if (analyticsRef) {
        fire(analyticsRef);
      } else {
        import('./lib/analytics').then((mod) => {
          analyticsRef = mod;
          fire(mod);
        }).catch(() => { /* swallow */ });
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);
}

// Global WhatsApp click delegate — wa.me / api.whatsapp.com / whatsapp:// links → whatsapp_click (deduped in analytics).
function useGlobalWhatsappClickTracking() {
  useEffect(() => {
    const handler = (e) => {
      const a = e.target && e.target.closest && e.target.closest('a[href]');
      if (!a || /^\/(crm|admin)/.test(window.location.pathname)) return;
      const href = a.getAttribute('href') || '';
      const m = href.match(/^(?:https?:\/\/)?(wa\.me|api\.whatsapp\.com|(?:www\.)?whatsapp\.com\/send)|^whatsapp:/i);
      if (!m) return;
      const host = (m[1] || 'whatsapp-app').toLowerCase().replace(/^www\./, '').split('/')[0];
      import('./lib/analytics').then((mod) => {
        try { mod.trackWhatsappClick({ label: a.dataset.trackLabel || a.dataset.testid || 'global-delegate', linkHost: host }); } catch (_) { /* never break UX */ }
      }).catch(() => {});
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);
}

// Eager-load homepage for fast LCP
import CleanHome from "./pages/CleanHome";
import CookieConsent from "./components/CookieConsent";

// Lazy-load chatbot so heavy chat SDK + state doesn't block initial paint (TBT fix)
const ChatBotGate = lazy(() => import("./components/ChatBotGate"));

// Lazy-load other routes
const LocationTemplate = lazy(() => import("./components/LocationTemplate"));
const CountyTemplate = lazy(() => import("./components/CountyTemplate"));
const CountyFaqsTemplate = lazy(() => import("./components/CountyFaqsTemplate"));
const ServiceTemplate = lazy(() => import("./components/ServiceTemplate"));
const ServiceLocationTemplate = lazy(() => import("./components/ServiceLocationTemplate"));
const FAQs = lazy(() => import("./pages/FAQs"));
const Services = lazy(() => import("./pages/Services"));
const BirminghamRemovals = lazy(() => import("./pages/BirminghamRemovals"));
const UxbridgeRemovals = lazy(() => import("./pages/UxbridgeRemovals"));
const BurnhamRemovals = lazy(() => import("./pages/BurnhamRemovals"));
const OxfordshireRemovals = lazy(() => import("./pages/OxfordshireRemovals"));
const MaidenheadPage = lazy(() => import("./pages/MaidenheadPage"));
const ReadingPage = lazy(() => import("./pages/ReadingPage"));
const MiltonKeynesPage = lazy(() => import("./pages/MiltonKeynesPage"));
const LondonPage = lazy(() => import("./pages/LondonPage"));
const Careers = lazy(() => import("./pages/Careers"));
const OurFleet = lazy(() => import("./pages/OurFleet"));
const MoveCostEstimator = lazy(() => import("./pages/MoveCostEstimator"));
const AISearchAnswers = lazy(() => import("./pages/AISearchAnswers"));
const BranchSubPage = lazy(() => import("./components/BranchSubPage"));
const BranchBlogPost = lazy(() => import("./components/BranchBlogPost"));
const FindABranch = lazy(() => import("./pages/FindABranch"));
const GetAQuote = lazy(() => import("./pages/GetAQuote"));
const LongDistanceMoves = lazy(() => import("./pages/LongDistanceMoves"));
const ShortDistanceMoves = lazy(() => import("./pages/ShortDistanceMoves"));
const RemovalsAndStorage = lazy(() => import("./pages/RemovalsAndStorage"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const BookingWidget = lazy(() => import("./pages/BookingWidget"));
const LegalPageTemplate = lazy(() => import("./components/LegalPageTemplate"));
const BlogPostTemplate = lazy(() => import("./components/BlogPostTemplate"));
const BlogList = lazy(() => import("./pages/BlogList"));
const AreasWeCover = lazy(() => import("./pages/AreasWeCover"));
const Contact = lazy(() => import("./pages/Contact"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminBookings = lazy(() => import("./pages/AdminBookings"));
const CRM = lazy(() => import("./pages/CRM"));
const QuotePage = lazy(() => import("./pages/QuotePage"));
const SiteVisitPage = lazy(() => import("./pages/SiteVisitPage"));
const CallbackPage = lazy(() => import("./pages/CallbackPage"));
const BookViewingPage = lazy(() => import("./pages/BookViewingPage"));
const MediaUploadPage = lazy(() => import("./pages/MediaUploadPage"));
const TrackPage = lazy(() => import("./pages/TrackPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Defer-mount wrapper: only mounts children after the browser is idle,
// keeping Total Blocking Time (TBT) low on first paint.
function DeferUntilIdle({ children, delay = 1500 }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const schedule = () => {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(() => { if (!cancelled) setReady(true); }, { timeout: delay });
      } else {
        setTimeout(() => { if (!cancelled) setReady(true); }, delay);
      }
    };
    // First user interaction also triggers mount (so chat is ready the moment they scroll/click)
    const onInteract = () => { if (!cancelled) setReady(true); };
    window.addEventListener('scroll', onInteract, { once: true, passive: true });
    window.addEventListener('pointerdown', onInteract, { once: true, passive: true });
    schedule();
    return () => {
      cancelled = true;
      window.removeEventListener('scroll', onInteract);
      window.removeEventListener('pointerdown', onInteract);
    };
  }, [delay]);
  return ready ? children : null;
}

// Pre-rendered HTML (#hg-snapshot, baked in at build time) stays visible until the real route commits.
const hasSnapshot = () => typeof document !== 'undefined' && !!document.getElementById('hg-snapshot');
function SnapshotCleanup() {
  useLayoutEffect(() => {
    document.getElementById('hg-snapshot')?.remove();
    document.querySelectorAll('[data-hg-snap]').forEach((n) => n.remove());
  }, []);
  return null;
}

const Loader = () => (hasSnapshot() ? null : (
  <div className="flex items-center justify-center min-h-screen bg-black">
    <div className="text-gold-400 text-2xl font-bold animate-pulse">Loading…</div>
  </div>
));

// Facebook Pixel: PageView on SPA route changes (first view is fired by the snippet in index.html).
function useFacebookPageViews() {
  useEffect(() => {
    let last = window.location.pathname;
    const fire = () => {
      const p = window.location.pathname;
      if (p === last) return;
      last = p;
      if (typeof window.fbq === 'function' && !/^\/(crm|admin)/.test(p)) window.fbq('track', 'PageView');
    };
    const wrap = (k) => { const o = window.history[k]; window.history[k] = function (...a) { const r = o.apply(this, a); setTimeout(fire, 0); return r; }; return o; };
    const push = wrap('pushState'), rep = wrap('replaceState');
    window.addEventListener('popstate', fire);
    return () => { window.history.pushState = push; window.history.replaceState = rep; window.removeEventListener('popstate', fire); };
  }, []);
}

function App() {
  useGlobalImageFallback();
  useGlobalPhoneClickTracking();
  useGlobalWhatsappClickTracking();
  useFacebookPageViews();
  // Real-user Web Vitals reporter (CLS / INP / LCP) — lazy-imports web-vitals
  // and beacons each metric to /api/analytics/web-vitals. Non-blocking.
  useEffect(() => {
    let cancelled = false;
    const start = () => {
      if (cancelled) return;
      import('./lib/webVitals').then(({ default: startWebVitalsReporter }) => {
        if (!cancelled) startWebVitalsReporter();
      }).catch(() => { /* swallow */ });
      // Flush any quote requests that were queued while the visitor was offline.
      Promise.all([import('./lib/offlineQueue'), import('./lib/apiBase')]).then(([oq, ab]) => {
        if (!cancelled) oq.initOfflineQueue(ab.API_BASE);
      }).catch(() => { /* swallow */ });
    };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(start, { timeout: 3000 });
    } else {
      setTimeout(start, 2000);
    }
    return () => { cancelled = true; };
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* Global site-name signals — injected on EVERY route so Google's SERP
            site-name algorithm always sees a consistent brand label, regardless
            of which URL Googlebot lands on. Per-page Helmets only override the
            specific tags they need (title, description, og:title…). */}
        <Helmet>
          <meta name="application-name" content="HomeGo Removals & Packing Ltd" />
          <meta name="apple-mobile-web-app-title" content="HomeGo Removals & Packing Ltd" />
          <meta property="og:site_name" content="HomeGo Removals & Packing Ltd" />
        </Helmet>
        <div className="App min-h-screen bg-black">
          <TrailingSlashRedirect />
          <Suspense fallback={<Loader />}>
            <SnapshotCleanup />
            <Routes>
              <Route path="/" element={<CleanHome />} />
              {/* Legacy/ghost URL 301-style redirects */}
              {LEGACY_REDIRECTS.map((r) => (
                <Route key={r.from} path={r.from} element={<KeepQueryNavigate to={r.to} replace />} />
              ))}
              {/* Discontinued service: man-and-van combo pages → matching house-removals page */}
              {locations.map((loc) => (
                <Route
                  key={`man-and-van-${loc.slug}`}
                  path={`/man-and-van-${loc.slug}`}
                  element={<KeepQueryNavigate to={`/house-removals-${loc.slug}`} replace />}
                />
              ))}
              {/* Renamed service: office-removals → office-relocation. Redirect old combo URLs to new. */}
              {locations.map((loc) => (
                <Route
                  key={`office-removals-${loc.slug}`}
                  path={`/office-removals-${loc.slug}`}
                  element={<KeepQueryNavigate to={`/office-relocation-${loc.slug}`} replace />}
                />
              ))}
              <Route path="/areas-we-cover" element={<AreasWeCover />} />
              <Route path="/find-a-branch" element={<FindABranch />} />
              <Route path="/branches" element={<FindABranch />} />
              <Route path="/locator" element={<FindABranch />} />
              <Route path="/services" element={<Services />} />
              <Route path="/birmingham-removals" element={<BirminghamRemovals />} />
              <Route path="/uxbridge-removals" element={<UxbridgeRemovals />} />
              <Route path="/burnham-removals" element={<BurnhamRemovals />} />
              <Route path="/oxfordshire-removals" element={<OxfordshireRemovals />} />
              {/* Custom Maidenhead branch page (Feb 2026) — must precede the
                  generic /removals-:slug LocationTemplate route below so the
                  bespoke MaidenheadPage is rendered instead. */}
              <Route path="/removals-maidenhead" element={<MaidenheadPage />} />
              <Route path="/maidenhead-removals" element={<KeepQueryNavigate to="/removals-maidenhead" replace />} />
              <Route path="/removals-maidenhead-sl6" element={<KeepQueryNavigate to="/removals-maidenhead" replace />} />
              <Route path="/house-removals-maidenhead-sl6" element={<KeepQueryNavigate to="/house-removals-maidenhead" replace />} />
              {/* Reading branch bespoke landing page + aliases */}
              <Route path="/removals-reading" element={<ReadingPage />} />
              <Route path="/reading-removals" element={<KeepQueryNavigate to="/removals-reading" replace />} />
              <Route path="/removals-reading-rg1" element={<KeepQueryNavigate to="/removals-reading" replace />} />
              {/* Milton Keynes branch bespoke landing page + aliases */}
              <Route path="/removals-milton-keynes" element={<MiltonKeynesPage />} />
              {/* London branch bespoke landing page + aliases.
                  NOTE: /removals-london stays as the Greater-London CountyTemplate hub. */}
              <Route path="/london-removals" element={<LondonPage />} />
              <Route path="/london-branch" element={<KeepQueryNavigate to="/london-removals" replace />} />
              <Route path="/removals-london-wc1" element={<KeepQueryNavigate to="/london-removals" replace />} />
              <Route path="/central-london-removals" element={<KeepQueryNavigate to="/london-removals" replace />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/our-fleet" element={<OurFleet />} />
              <Route path="/fleet" element={<KeepQueryNavigate to="/our-fleet" replace />} />
              <Route path="/vehicles" element={<KeepQueryNavigate to="/our-fleet" replace />} />
              <Route path="/trucks" element={<KeepQueryNavigate to="/our-fleet" replace />} />
              <Route path="/vans" element={<KeepQueryNavigate to="/our-fleet" replace />} />
              <Route path="/7-tonne-truck" element={<KeepQueryNavigate to="/our-fleet" replace />} />
              <Route path="/jobs" element={<KeepQueryNavigate to="/careers" replace />} />
              <Route path="/apply" element={<KeepQueryNavigate to="/careers" replace />} />
              <Route path="/hiring" element={<KeepQueryNavigate to="/careers" replace />} />
              <Route path="/milton-keynes" element={<KeepQueryNavigate to="/removals-milton-keynes" replace />} />
              <Route path="/milton-keynes-removals" element={<KeepQueryNavigate to="/removals-milton-keynes" replace />} />
              <Route path="/miltonkeynes-removals" element={<KeepQueryNavigate to="/removals-milton-keynes" replace />} />
              <Route path="/mk-removals" element={<KeepQueryNavigate to="/removals-milton-keynes" replace />} />
              <Route path="/removals-mk9" element={<KeepQueryNavigate to="/removals-milton-keynes" replace />} />
              <Route path="/removals-cmk" element={<KeepQueryNavigate to="/removals-milton-keynes" replace />} />
              {/* Apex-style /branch/[city]/ aliases — redirect to canonical URLs.
                  e.g. /branch/burnham → /burnham-removals, /branch/slough → /removals-slough,
                  /branch/oxford → /oxfordshire-removals. Anything else → /find-a-branch. */}
              <Route path="/branch" element={<KeepQueryNavigate to="/find-a-branch" replace />} />
              <Route path="/branch/" element={<KeepQueryNavigate to="/find-a-branch" replace />} />
              <Route path="/branch/:slug" element={<BranchAlias />} />
              <Route path="/branch/:slug/" element={<BranchAlias />} />
              {/* Legacy old-site URL patterns crawled by Google → resolve to live pages */}
              <Route path="/town/:slug" element={<TownCityAlias />} />
              <Route path="/city/:slug" element={<TownCityAlias />} />
              <Route path="/removals/:slug" element={<TownCityAlias />} />
              <Route path="/book/:slug" element={<BranchAlias />} />
              {/* Move cost estimator — instant £ range calculator */}
              <Route path="/move-cost-estimator" element={<MoveCostEstimator />} />
              {/* AEO hub — 50+ AI-extractable Q&As for ChatGPT/Gemini/Perplexity */}
              <Route path="/ai-answers" element={<AISearchAnswers />} />
              <Route path="/ai" element={<KeepQueryNavigate to="/ai-answers" replace />} />
              <Route path="/ask" element={<KeepQueryNavigate to="/ai-answers" replace />} />
              {/* Common alt spellings & redirects to the canonical estimator URL */}
              <Route path="/cost-calculator" element={<KeepQueryNavigate to="/move-cost-estimator" replace />} />
              <Route path="/removal-cost-calculator" element={<KeepQueryNavigate to="/move-cost-estimator" replace />} />
              <Route path="/estimate" element={<KeepQueryNavigate to="/move-cost-estimator" replace />} />
              <Route path="/estimator" element={<KeepQueryNavigate to="/move-cost-estimator" replace />} />
              <Route path="/quote-calculator" element={<KeepQueryNavigate to="/move-cost-estimator" replace />} />
              {/* Common alt spellings for the Oxfordshire branch */}
              <Route path="/oxford-removals" element={<KeepQueryNavigate to="/oxfordshire-removals" replace />} />
              <Route path="/littlemore-removals" element={<KeepQueryNavigate to="/oxfordshire-removals" replace />} />

              {/* Branch sub-pages — same pattern for all 4 branches */}
              {['burnham', 'birmingham', 'uxbridge', 'oxfordshire'].map((b) => (
                ['areas', 'services', 'contact', 'about', 'faqs', 'blog'].map((p) => (
                  <Route
                    key={`${b}-${p}`}
                    path={`/${b}-removals/${p}`}
                    element={<BranchSubPage branchKey={b} pageType={p} />}
                  />
                ))
              ))}
              {/* Maidenhead branch sub-pages — same 6 page types, nested
                  under /removals-maidenhead (matches the bespoke landing page URL) */}
              {['areas', 'services', 'contact', 'about', 'faqs', 'blog'].map((p) => (
                <Route
                  key={`maidenhead-${p}`}
                  path={`/removals-maidenhead/${p}`}
                  element={<BranchSubPage branchKey="maidenhead" pageType={p} />}
                />
              ))}
              {/* Reading branch sub-pages — same pattern, nested under
                  /removals-reading (matches the bespoke landing page URL) */}
              {['areas', 'services', 'contact', 'about', 'faqs', 'blog'].map((p) => (
                <Route
                  key={`reading-${p}`}
                  path={`/removals-reading/${p}`}
                  element={<BranchSubPage branchKey="reading" pageType={p} />}
                />
              ))}
              {/* Milton Keynes branch sub-pages — same pattern as Maidenhead/Reading,
                  nested under /removals-milton-keynes */}
              {['areas', 'services', 'contact', 'about', 'faqs', 'blog'].map((p) => (
                <Route
                  key={`miltonkeynes-${p}`}
                  path={`/removals-milton-keynes/${p}`}
                  element={<BranchSubPage branchKey="miltonkeynes" pageType={p} />}
                />
              ))}
              {/* London branch sub-pages — nested under /london-removals */}
              {['areas', 'services', 'contact', 'about', 'faqs', 'blog'].map((p) => (
                <Route
                  key={`london-${p}`}
                  path={`/london-removals/${p}`}
                  element={<BranchSubPage branchKey="london" pageType={p} />}
                />
              ))}
              {/* Branch blog post routes — 12 per branch (Oxfordshire posts seeded as new branch launches) */}
              {['burnham', 'birmingham', 'uxbridge', 'oxfordshire'].map((b) => (
                <Route
                  key={`${b}-blog-post`}
                  path={`/${b}-removals/blog/:slug`}
                  element={<BranchBlogPost branchKey={b} />}
                />
              ))}
              <Route
                key="maidenhead-blog-post"
                path="/removals-maidenhead/blog/:slug"
                element={<BranchBlogPost branchKey="maidenhead" />}
              />
              <Route
                key="reading-blog-post"
                path="/removals-reading/blog/:slug"
                element={<BranchBlogPost branchKey="reading" />}
              />
              <Route
                key="miltonkeynes-blog-post"
                path="/removals-milton-keynes/blog/:slug"
                element={<BranchBlogPost branchKey="miltonkeynes" />}
              />
              <Route
                key="london-blog-post"
                path="/london-removals/blog/:slug"
                element={<BranchBlogPost branchKey="london" />}
              />
              <Route path="/book/:branchKey" element={<BookingWidget />} />
              <Route path="/get-a-quote" element={<GetAQuote />} />
              <Route path="/long-distance-removals" element={<LongDistanceMoves />} />
              {/* Common alt spellings */}
              <Route path="/long-distance-moves" element={<KeepQueryNavigate to="/long-distance-removals" replace />} />
              <Route path="/long-distance" element={<KeepQueryNavigate to="/long-distance-removals" replace />} />
              <Route path="/nationwide-removals" element={<KeepQueryNavigate to="/long-distance-removals" replace />} />
              <Route path="/short-distance-removals" element={<ShortDistanceMoves />} />
              <Route path="/removals-and-storage" element={<RemovalsAndStorage />} />
              <Route path="/short-distance-moves" element={<KeepQueryNavigate to="/short-distance-removals" replace />} />
              <Route path="/short-distance" element={<KeepQueryNavigate to="/short-distance-removals" replace />} />
              <Route path="/local-removals" element={<KeepQueryNavigate to="/short-distance-removals" replace />} />
              <Route path="/same-day-removals" element={<KeepQueryNavigate to="/short-distance-removals" replace />} />
              <Route path="/thank-you" element={<ThankYou />} />
              {/* Common alt spellings ad copy / emails may use */}
              <Route path="/thanks" element={<KeepQueryNavigate to="/thank-you" replace />} />
              <Route path="/thankyou" element={<KeepQueryNavigate to="/thank-you" replace />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faqs" element={<FAQs />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPostTemplate />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/bookings" element={<ProtectedRoute><AdminBookings /></ProtectedRoute>} />
              <Route path="/crm/*" element={<ProtectedRoute><CRM /></ProtectedRoute>} />
              <Route path="/quote/:token" element={<QuotePage />} />
              <Route path="/site-visit/:token" element={<SiteVisitPage />} />
              <Route path="/callback/:token" element={<CallbackPage />} />
              <Route path="/book-viewing" element={<BookViewingPage />} />
              <Route path="/upload-media/:token" element={<MediaUploadPage />} />
              <Route path="/track/:token" element={<TrackPage />} />
              <Route path="/admin" element={<ProtectedRoute><AdminBookings /></ProtectedRoute>} />
              {countySlugs.map((slug) => (
                <Route key={slug} path={`/removals-${slug}`} element={<CountyTemplate />} />
              ))}
              {/* Per-county FAQ pages (May 2026) — auto-generated for every county hub.
                  URL pattern: /faqs-{slug} (e.g. /faqs-london, /faqs-essex, /faqs-berkshire) */}
              {countySlugs.map((slug) => (
                <Route key={`faqs-${slug}`} path={`/faqs-${slug}`} element={<CountyFaqsTemplate />} />
              ))}
              {/* NOTE: london is excluded — /removals-london is the Greater-London
                  COUNTY hub (CountyTemplate); the London BRANCH lives at /london-removals. */}
              {locations.filter((loc) => loc.slug !== 'london').map((loc) => (
                <Route key={loc.slug} path={`/removals-${loc.slug}`} element={<LocationTemplate />} />
              ))}
              {allServiceSlugs.map((slug) => (
                <Route key={slug} path={`/${slug}`} element={<ServiceTemplate />} />
              ))}
              {/* Service × Location combo pages — 5 services × 73 locations = 365 SEO landing pages */}
              {allServiceSlugs.flatMap((svcSlug) =>
                locations.map((loc) => (
                  <Route
                    key={`${svcSlug}-${loc.slug}`}
                    path={`/${svcSlug}-${loc.slug}`}
                    element={<ServiceLocationTemplate />}
                  />
                ))
              )}
              {allLegalSlugs.map((slug) => (
                <Route key={slug} path={`/${slug}`} element={<LegalPageTemplate />} />
              ))}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
          <DeferUntilIdle>
            <Suspense fallback={null}>
              <ChatBotGate />
            </Suspense>
          </DeferUntilIdle>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
